// Import Quill styles
import "react-quill/dist/quill.snow.css";

import {
  Alert,
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { memo, useEffect, useRef, useState } from "react";
import {
  ArtifactService,
  useArtifactContent,
} from "../../services/ArtifactService";

import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ReactQuill from "react-quill";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";

// Helper functions for markdown/HTML conversion
const markdownToHtml = (markdown: string): string => {
  // Simple markdown to HTML conversion for common elements
  let html = markdown
    // Headers
    .replace(/^### (.*$)/gim, "<h3>$1</h3>")
    .replace(/^## (.*$)/gim, "<h2>$1</h2>")
    .replace(/^# (.*$)/gim, "<h1>$1</h1>")
    // Bold
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.*?)__/g, "<strong>$1</strong>")
    // Italic
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/_(.*?)_/g, "<em>$1</em>")
    // Code blocks
    .replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
    // Lists
    .replace(/^\* (.*$)/gim, "<li>$1</li>")
    .replace(/^- (.*$)/gim, "<li>$1</li>")
    .replace(/^\d+\. (.*$)/gim, "<li>$1</li>")
    // Line breaks
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br>");

  // Wrap in paragraphs and handle lists
  html = "<p>" + html + "</p>";
  html = html.replace(/<p><\/p>/g, "");
  html = html.replace(/(<li>.*<\/li>)/gs, "<ul>$1</ul>");

  return html;
};

const htmlToMarkdown = (html: string): string => {
  // Simple HTML to markdown conversion
  let markdown = html
    // Headers
    .replace(/<h1>(.*?)<\/h1>/g, "# $1\n\n")
    .replace(/<h2>(.*?)<\/h2>/g, "## $1\n\n")
    .replace(/<h3>(.*?)<\/h3>/g, "### $1\n\n")
    .replace(/<h4>(.*?)<\/h4>/g, "#### $1\n\n")
    .replace(/<h5>(.*?)<\/h5>/g, "##### $1\n\n")
    .replace(/<h6>(.*?)<\/h6>/g, "###### $1\n\n")
    // Bold and italic
    .replace(/<strong>(.*?)<\/strong>/g, "**$1**")
    .replace(/<b>(.*?)<\/b>/g, "**$1**")
    .replace(/<em>(.*?)<\/em>/g, "*$1*")
    .replace(/<i>(.*?)<\/i>/g, "*$1*")
    // Code
    .replace(/<pre><code>([\s\S]*?)<\/code><\/pre>/g, "```\n$1\n```\n\n")
    .replace(/<code>(.*?)<\/code>/g, "`$1`")
    // Links
    .replace(/<a href="([^"]*)"[^>]*>(.*?)<\/a>/g, "[$2]($1)")
    // Lists
    .replace(/<ul><li>(.*?)<\/li><\/ul>/gs, (_, content) => {
      return content
        .replace(/<\/li><li>/g, "\n- ")
        .replace(/^/, "- ")
        .replace(/$/, "\n\n");
    })
    .replace(/<ol><li>(.*?)<\/li><\/ol>/gs, (_, content) => {
      let counter = 1;
      return content
        .replace(/<\/li><li>/g, () => `\n${++counter}. `)
        .replace(/^/, "1. ")
        .replace(/$/, "\n\n");
    })
    // Paragraphs and breaks
    .replace(/<\/p><p>/g, "\n\n")
    .replace(/<p>/g, "")
    .replace(/<\/p>/g, "\n\n")
    .replace(/<br\s*\/?>/g, "\n")
    // Clean up extra whitespace
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return markdown;
};

interface ArtifactFullViewerProps {
  open: boolean;
  onClose: () => void;
  artifactId: string | number;
  artifactName?: string;
}

const ArtifactFullViewer: React.FC<ArtifactFullViewerProps> = memo(
  ({ open, onClose, artifactId, artifactName }) => {
    const printRef = useRef<HTMLDivElement>(null);
    const [quillContent, setQuillContent] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const {
      data: content,
      isLoading,
      error,
      refetch,
    } = useArtifactContent(artifactId);

    // Initialize Quill content when data loads
    useEffect(() => {
      if (content && !isLoading && !error) {
        // Check if content is markdown and convert to HTML for Quill
        if (
          artifactName?.endsWith(".md") ||
          /\[.*\]\(.*\)/.test(content) || // Check for markdown links
          content.includes("# ") ||
          content.includes("## ") ||
          content.includes("**") ||
          content.includes("*") ||
          content.includes("`")
        ) {
          // Convert markdown to HTML for Quill display
          setQuillContent(markdownToHtml(content));
        } else if (content.trim().startsWith("<")) {
          // Already HTML
          setQuillContent(content);
        } else {
          // Plain text - wrap in paragraphs
          setQuillContent(
            `<p>${content
              .replace(/\n\n/g, "</p><p>")
              .replace(/\n/g, "<br>")}</p>`,
          );
        }
      }
    }, [content, isLoading, error, artifactName]);

    const handlePrint = useReactToPrint({
      contentRef: printRef,
      documentTitle: artifactName || `Artifact ${artifactId}`,
      pageStyle: `
        @page {
          size: A4;
          margin: 0.75in;
        }
        @media print {
          body { font-size: 12pt; }
          h1 { font-size: 18pt; }
          h2 { font-size: 16pt; }
          h3 { font-size: 14pt; }
        }
      `,
    });

    const handleSave = async () => {
      if (!quillContent.trim()) {
        toast.error("Content cannot be empty");
        return;
      }

      setIsSaving(true);
      try {
        let contentToSave = quillContent;

        // If the original content was markdown, convert back to markdown
        if (
          artifactName?.endsWith(".md") ||
          (content &&
            (/\[.*\]\(.*\)/.test(content) || // Check for markdown links
              content.includes("# ") ||
              content.includes("## ") ||
              content.includes("**") ||
              content.includes("*") ||
              content.includes("`")))
        ) {
          contentToSave = htmlToMarkdown(quillContent);
        }

        await ArtifactService.updateArtifactContent(artifactId, contentToSave);
        toast.success("Artifact updated successfully!");
        refetch(); // Refresh the content
      } catch (error) {
        toast.error("Failed to save changes");
        console.error("Failed to save artifact:", error);
      } finally {
        setIsSaving(false);
      }
    };

    const handleQuillChange = (content: string) => {
      setQuillContent(content);
    };

    // Quill configuration - full toolbar without tables
    const quillModules = {
      toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
          { list: "ordered" },
          { list: "bullet" },
          { indent: "-1" },
          { indent: "+1" },
        ],
        ["link", "image", "video"],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ["code-block"],
        ["clean"],
      ],
    };

    const quillFormats = [
      "header",
      "font",
      "size",
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "list",
      "bullet",
      "indent",
      "link",
      "image",
      "video",
      "align",
      "color",
      "background",
      "code-block",
    ];

    const renderErrorContent = () => {
      if (
        content &&
        (content.includes("<!DOCTYPE html>") || content.includes("<html"))
      ) {
        return (
          <Alert severity="warning" sx={{ m: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>API Endpoint Issue Detected</strong>
            </Typography>
            <Typography variant="body2" gutterBottom>
              The artifact content API endpoint is not working correctly and is
              returning the main application HTML instead of the artifact
              content.
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Artifact ID:</strong> {artifactId}
            </Typography>
            <Typography variant="body2">
              Please check the backend API configuration for the artifacts
              endpoint.
            </Typography>
          </Alert>
        );
      }
      return null;
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: "95vw",
            height: "95vh",
            maxWidth: "none",
            maxHeight: "none",
          },
        }}
      >
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {artifactName || `Artifact ${artifactId}`}
            </Typography>

            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isSaving}
              sx={{ mr: 1 }}
              startIcon={
                isSaving ? <CircularProgress size={16} /> : <SaveIcon />
              }
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePrint}
              sx={{ mr: 2 }}
              startIcon={<span>⬇️</span>}
            >
              Export
            </Button>

            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading artifact content...
              </Typography>
            </Box>
          ) : error ? (
            <Box p={3}>
              <Alert severity="error">
                Failed to load artifact content: {(error as Error).message}
              </Alert>
            </Box>
          ) : renderErrorContent() ? (
            <Box p={3}>{renderErrorContent()}</Box>
          ) : (
            <Box
              ref={printRef}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                p: 2,
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  height: "calc(100vh - 200px)",
                }}
              >
                <ReactQuill
                  value={quillContent}
                  onChange={handleQuillChange}
                  modules={quillModules}
                  formats={quillFormats}
                  theme="snow"
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                  placeholder="Edit your content here..."
                />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);

ArtifactFullViewer.displayName = "ArtifactFullViewer";

export default ArtifactFullViewer;
